import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

const TenantSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to landing page if user came here without being authenticated
  useEffect(() => {
    // Check if user came from a protected route or if they're just visiting tenant-selection directly
    const from = location.state?.from?.pathname;
    if (!from || from === '/') {
      navigate('/', { replace: true });
    }
  }, [navigate, location.state]);
  const [tenantAlias, setTenantAlias] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTenantSubmit = e => {
    e.preventDefault();

    if (!tenantAlias.trim()) {
      setError('Please enter a tenant alias');
      return;
    }

    setLoading(true);
    setError('');

    // Small delay to show loading state, then redirect
    setTimeout(() => {
      const loginUrl = `/login?tenant=${encodeURIComponent(tenantAlias.trim())}`;
      navigate(loginUrl, { replace: true });
    }, 200);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        // Match the whitish background feel of the login experience
        backgroundColor: theme => theme.palette.background.default || '#f5f5f7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // Very subtle soft light accents, kept minimal on a light background
          background:
            'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.06) 0%, transparent 55%), radial-gradient(circle at 80% 20%, rgba(148, 163, 184, 0.08) 0%, transparent 55%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={18}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: theme => theme.palette.background.paper,
            backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            boxShadow:
              '0 20px 40px rgba(15, 23, 42, 0.35), 0 1px 3px rgba(15, 23, 42, 0.2)',
          }}
        >
          <Box
            sx={{
              // White-ish, panel-style header to match login card aesthetics
              backgroundColor: theme =>
                theme.palette.background.paper || '#ffffff',
              color: theme => theme.palette.text.primary,
              padding: 3,
              textAlign: 'center',
              borderBottom: theme =>
                `1px solid ${theme.palette.divider || 'rgba(148,163,184,0.3)'}`,
            }}
          >
            <BusinessIcon
              sx={theme => ({
                fontSize: 40,
                mb: 1.5,
                color: theme.palette.primary.main,
              })}
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Select Your Organization
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.9,
                mt: 1,
              }}
            >
              Choose your organization to continue to secure SSO login
            </Typography>
          </Box>

          <CardContent
            sx={{
              padding: 4,
              backgroundColor: theme => theme.palette.background.paper,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleTenantSubmit}>
              <TextField
                fullWidth
                label="Organization Alias"
                placeholder="Enter your organization alias"
                value={tenantAlias}
                onChange={e => setTenantAlias(e.target.value)}
                disabled={loading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: theme =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(15, 23, 42, 0.9)'
                        : 'rgba(249, 250, 251, 0.96)',
                  },
                }}
                helperText="This is usually your organization's unique identifier"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !tenantAlias.trim()}
                sx={{
                  width: '100%',
                  borderRadius: 999,
                  py: 1.6,
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  // Flat iOS-style primary button using theme primary color
                  backgroundColor: theme => theme.palette.primary.main,
                  color: theme =>
                    theme.palette.primary.contrastText || '#ffffff',
                  border: 'none',
                  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.25)',
                  '&:hover': {
                    backgroundColor: theme =>
                      theme.palette.primary.dark || theme.palette.primary.main,
                    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.35)',
                  },
                  '&:active': {
                    boxShadow: '0 3px 10px rgba(15, 23, 42, 0.25)',
                    transform: 'scale(0.985)',
                  },
                  '&:disabled': {
                    backgroundColor: theme =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(15,23,42,0.75)'
                        : 'rgba(148, 163, 184, 0.3)',
                    color: 'rgba(226,232,240,0.95)',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Continue to Login'
                )}
              </Button>
            </form>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 3 }}
            >
              Don't know your organization alias? Contact your administrator.
            </Typography>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

export default TenantSelection;
